export const AGENT_WORKSPACE_REQUIREMENT_SCHEMA_VERSION =
  "cogo.studio-agent-workspace-requirement.v1" as const;

export type AgentWorkspaceIntentKind =
  | "comment"
  | "requirement"
  | "fix"
  | "explain"
  | "validate"
  | "export"
  | "promote-rule";

export type AgentWorkspaceRequirementStatus =
  | "draft"
  | "active"
  | "patched"
  | "validated"
  | "resolved"
  | "blocked";

export type AgentWorkspaceRequirementActor =
  | "user"
  | "cogo-agent"
  | "codex-deterministic"
  | "qwen-reviewer"
  | "gemma-reviewer";

export type AgentWorkspaceRequirementThreadKind =
  | "note"
  | "agent-handoff"
  | "acceptance-criteria"
  | "proof";

export interface AgentWorkspaceRequirementThreadEntry {
  entryId: string;
  actor: AgentWorkspaceRequirementActor;
  kind: AgentWorkspaceRequirementThreadKind;
  body: string;
  createdAt: string;
}

export interface AgentWorkspaceRequirementProposal {
  body: string;
  source: "qwen-gateway" | "local-fallback";
  mode: "text-replacement" | "requirement";
  createdAt: string;
}

export interface AgentWorkspaceRequirement {
  schemaVersion: typeof AGENT_WORKSPACE_REQUIREMENT_SCHEMA_VERSION;
  requirementId: string;
  anchorId: string;
  intent: AgentWorkspaceIntentKind;
  createdBy: AgentWorkspaceRequirementActor;
  createdAt: string;
  body: string;
  acceptanceCriteria: string[];
  status: AgentWorkspaceRequirementStatus;
  thread: AgentWorkspaceRequirementThreadEntry[];
  semanticPatchIds: string[];
  proofRefs: string[];
  promotedRuleIds: string[];
  proposal?: AgentWorkspaceRequirementProposal;
}

export function createAgentWorkspaceRequirementDraft(input: {
  requirementId: string;
  anchorId: string;
  intent: AgentWorkspaceIntentKind;
  body: string;
  acceptanceCriteria?: string[];
  createdBy?: AgentWorkspaceRequirementActor;
  createdAt?: string;
  proposal?: Omit<AgentWorkspaceRequirementProposal, "createdAt"> & {
    createdAt?: string;
  };
}): AgentWorkspaceRequirement {
  const createdBy = input.createdBy ?? "user";
  const createdAt = input.createdAt ?? new Date().toISOString();
  const acceptanceCriteria = input.acceptanceCriteria ?? [];
  const proposal = input.proposal
    ? {
        ...input.proposal,
        createdAt: input.proposal.createdAt ?? createdAt,
      }
    : undefined;
  return {
    schemaVersion: AGENT_WORKSPACE_REQUIREMENT_SCHEMA_VERSION,
    requirementId: input.requirementId,
    anchorId: input.anchorId,
    intent: input.intent,
    createdBy,
    createdAt,
    body: input.body,
    acceptanceCriteria,
    status: "draft",
    thread: [
      {
        entryId: `${input.requirementId}-user-note`,
        actor: createdBy,
        kind: "note",
        body: input.body,
        createdAt,
      },
      {
        entryId: `${input.requirementId}-agent-handoff`,
        actor: "cogo-agent",
        kind: "agent-handoff",
        body:
          "Prepare a proposed change from this anchored requirement; do not apply it without review.",
        createdAt,
      },
    ],
    semanticPatchIds: [],
    proofRefs: [],
    promotedRuleIds: [],
    ...(proposal ? { proposal } : {}),
  };
}
